import { createContext, useCallback, useContext, useState } from 'react';
import ChecklistCreator from '../components/ChecklistCreator';

/* Singleton context for the Hyatt-branded checklist builder.

   Why a context?
   The previous design rendered <ChecklistCreator/> inline INSIDE the
   parent Radix Dialog (WorkspaceDialog or PropertyBudgetsDialog). Radix
   listens on document-level pointerdown events to detect outside-clicks
   and close itself. Our checklist modal — even when wrapped in
   createPortal — registers as "outside" the parent Dialog, so the very
   first click on Export PDF / XLSX / CSV closed the WorkspaceDialog and
   unmounted the modal before axios could fire (the user-reported "create
   checklist doesn't work" bug).

   This context mounts ONE checklist instance globally at App level
   (alongside HeavenlyAssistant) so it lives entirely outside any Radix
   Dialog tree. Children open it via `useChecklistCreator().open(opts)`.
*/

const ChecklistContext = createContext({
  open: () => {},
});

export function useChecklistCreator() {
  return useContext(ChecklistContext);
}

export function ChecklistCreatorProvider({ children }) {
  const [config, setConfig] = useState(null); // { workspaceLabel, adminToken } | null

  const open = useCallback((opts) => {
    setConfig({
      workspaceLabel: opts?.workspaceLabel || '',
      adminToken: opts?.adminToken || (typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null),
      initialItems: opts?.initialItems || [],
    });
  }, []);

  const close = useCallback(() => setConfig(null), []);

  return (
    <ChecklistContext.Provider value={{ open }}>
      {children}
      <ChecklistCreator
        open={!!config}
        onClose={close}
        workspaceLabel={config?.workspaceLabel}
        adminToken={config?.adminToken}
        initialItems={config?.initialItems}
      />
    </ChecklistContext.Provider>
  );
}
